import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../routes/all_routes";
import { authService } from "../../services";
import { Modal } from "antd";

const MainLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const currentUser = authService.getCurrentUser();

  // Super Admin Navigation - Full access
  const superAdminNavigation = [
    {
      name: "Dashboard",
      href: all_routes.adminDashboard,
      current: location.pathname === all_routes.adminDashboard,
    },
    {
      name: "Companies",
      href: all_routes.adminCompanies,
      current: location.pathname === all_routes.adminCompanies,
    },
    {
      name: "Users",
      href: all_routes.adminUsers,
      current: location.pathname === all_routes.adminUsers,
    },
    {
      name: "Guest Links",
      href: all_routes.adminGuestLinks,
      current: location.pathname === all_routes.adminGuestLinks,
    },
    {
      name: "Testimonials",
      href: all_routes.adminTestimonials,
      current: location.pathname === all_routes.adminTestimonials,
    },
  ];

  // Admin Navigation - Company admin only
  const adminNavigation = [
    {
      name: "Dashboard",
      href: all_routes.adminDashboard,
      current: location.pathname === all_routes.adminDashboard,
    },
    {
      name: "Users",
      href: all_routes.adminUsers,
      current: location.pathname === all_routes.adminUsers,
    },
    {
      name: "Guest Links",
      href: all_routes.adminGuestLinks,
      current: location.pathname === all_routes.adminGuestLinks,
    },
    {
      name: "Testimonials",
      href: all_routes.adminTestimonials,
      current: location.pathname === all_routes.adminTestimonials,
    },
  ];

  // User Navigation - For regular users
  const userNavigation = [
    {
      name: "Dashboard",
      href: all_routes.userDashboard,
      current: location.pathname === all_routes.userDashboard,
    },
    {
      name: "Record Testimonial",
      href: all_routes.userRecord,
      current: location.pathname === all_routes.userRecord,
    },
  ];

  // Get navigation based on user role
  const getNavigation = () => {
    if (currentUser?.role === "super_admin") {
      return superAdminNavigation;
    } else if (currentUser?.role === "admin") {
      return adminNavigation;
    } else {
      return userNavigation;
    }
  };

  const navigation = getNavigation();

  // Get role display name
  const getRoleDisplay = () => {
    switch (currentUser?.role) {
      case "super_admin":
        return "Super Administrator";
      case "admin":
        return "Administrator";
      case "user":
        return "User";
      default:
        return "User";
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    authService.logout();
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-xl font-bold text-gray-900">
                  Testimonial Platform
                </h1>
              </div>
              <div className="hidden md:ml-6 md:flex md:space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      item.current
                        ? "border-blue-500 text-gray-900"
                        : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {/* User info */}
              {currentUser && (
                <div className="flex items-center space-x-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      currentUser.role === "super_admin"
                        ? "bg-purple-500"
                        : currentUser.role === "admin"
                        ? "bg-red-500"
                        : "bg-blue-500"
                    }`}
                  >
                    <span className="text-white text-sm font-medium">
                      {currentUser.name
                        ? currentUser.name.charAt(0).toUpperCase()
                        : "U"}
                    </span>
                  </div>
                  <div className="hidden sm:block">
                    <div className="text-sm font-medium text-gray-900">
                      {currentUser.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getRoleDisplay()}
                    </div>
                  </div>
                </div>
              )}

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</main>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Logout
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to logout?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;
